import { useEffect, useState, useCallback, useContext } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import styles from './quiz-thank-you-and-password.module.scss';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import QuizInfoTemplate from '../../../views/Quiz/QuizInfoTemplate/QuizInfoTemplate';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import HTMLReactParser from 'html-react-parser';

const QuizThankYouAndPassword = () => {
    const { currentQuestion, handleSubmitAnswer, state } =
        useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        additionalField1: subHeader,
        additionalField2: subDescription,
        answerList,
        // userAnswers,
        medication,
        questionImage,
    } = currentQuestion || {};
    const { frontendStamp: answerId } = answerList[0] || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');

    const validateConfirmPassword = useCallback(() => {
        let errorMessage = '';
        if (confirmPassword === '') {
            errorMessage = 'This field is required';
        } else if (confirmPassword !== password) {
            errorMessage = 'Passwords do not match';
        }
        setConfirmPasswordError(errorMessage);

        return !errorMessage;
    }, [password, confirmPassword]);

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
    };

    const handleConfirmPasswordChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const value = e.target.value;
        setConfirmPassword(value);
    };

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (password.length > 0 && !continueDisabled && questionId) {
            setContinueDisabled(true);
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: answerId ? [answerId] : undefined,
                answerTexts: [password],
                medication: medication ? medication : '',
            });
        }
    }, [
        continueDisabled,
        password,
        questionId,
        questionText,
        answerId,
        medication,
        handleSubmitAnswer,
        setContinueDisabled,
    ]);

    // Continue click listener
    useEffect(() => {
        const handleContinueClick = () => {
            if (validateConfirmPassword()) {
                handleSubmit();
            }
        };
        window.addEventListener(
            QUIZ_EVENT_TYPES.continueClick,
            handleContinueClick,
        );

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleContinueClick,
            );
    }, [handleSubmit, validateConfirmPassword]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && validateConfirmPassword()) {
                handleSubmit();
            }
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit, validateConfirmPassword]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            if (password && confirmPassword && password === confirmPassword) {
                setContinueDisabled(!validateConfirmPassword());
            } else {
                setContinueDisabled(false);
            }
        }
    }, [
        password,
        pageTransitioning,
        setContinueDisabled,
        validateConfirmPassword,
        confirmPassword,
    ]);

    const handleConfirmPasswordFocus = () => {
        setConfirmPasswordError('');
    };
    const handleConfirmPasswordBlur = () => {
        setConfirmPasswordError('');
        validateConfirmPassword();
    };

    return (
        <QuizInfoTemplate contentClassName={styles['quiz--loading-content']}>
            <div className={classNames(styles['quiz--section-break'])}>
                {questionImage ? (
                    <div className={styles['icon-wrapper']}>
                        <img
                            className={styles['icon']}
                            src={questionImage}
                            alt={questionText}
                        />
                    </div>
                ) : null}
                <span className={styles['section-wrapper']}>
                    <h2 className={styles['title']}>
                        {HTMLReactParser(questionText)}
                    </h2>
                    <span className={styles['description']}>
                        {questionExplanation}
                    </span>
                </span>
                {subHeader && subDescription ? (
                    <span className={styles['password-wrapper']}>
                        <h4 className={styles['subtitle']}>{subHeader}</h4>
                        <span
                            className={classNames(
                                styles['description'],
                                'text-grey-dark',
                            )}
                        >
                            {subDescription}
                        </span>
                    </span>
                ) : null}
                <div className={classNames(styles['form--wrapper'])}>
                    <PasswordField
                        data-testid="password-input"
                        label="Create password"
                        placeholder="Create password..."
                        value={password}
                        onChange={handlePasswordChange}
                    />
                    <PasswordField
                        data-testid="confirm-password-input"
                        label="Confirm password"
                        placeholder="Confirm password..."
                        value={confirmPassword}
                        errorMessage={confirmPasswordError}
                        onChange={handleConfirmPasswordChange}
                        onFocus={handleConfirmPasswordFocus}
                        onBlur={handleConfirmPasswordBlur}
                    />
                </div>
            </div>
        </QuizInfoTemplate>
    );
};

export type PasswordFieldProps = {
    placeholder?: string;
    label?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    value?: string;
    errorMessage?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const PasswordField = ({
    label,
    placeholder = 'Enter text here...',
    onChange,
    onFocus,
    onBlur,
    value,
    errorMessage,
    ...rest
}: PasswordFieldProps) => {
    return (
        <>
            <div className={classNames(styles['quiz-field-header'])}>
                {label ? <label className="p2 highlight">{label}</label> : null}
                <input
                    {...rest}
                    className={classNames(
                        styles['default-field'],
                        errorMessage ? styles['error-field'] : '',
                        'resize',
                    )}
                    value={value}
                    type="password"
                    placeholder={placeholder}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                />
                {errorMessage && (
                    <p className={classNames(styles['error-message'])}>
                        <FontAwesomeIcon icon={faTriangleExclamation} />
                        {errorMessage}
                    </p>
                )}
            </div>
        </>
    );
};

export default QuizThankYouAndPassword;
